import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Select,
  message,
  Space,
  Divider,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { customerApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCapability } from './auth/RoleGuard';
import { useSessionStashedForm } from '../contexts/SessionGuardContext';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Helper function to get role-based path prefix
const getRolePrefix = (userRole?: string): string => {
  return userRole === 'customer' ? '/portal' :
         userRole === 'technician' ? '/technician' :
         userRole === 'advisor' ? '/advisor' :
         userRole === 'owner' ? '/owner' : '/admin';
};

const CustomerForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const rolePrefix = getRolePrefix(user?.role);
  const canEditCustomers = useCapability('edit_customers');
  const isEditing = Boolean(id);
  useSessionStashedForm(form, id ? `customer-${id}` : 'customer-new');

  useEffect(() => {
    if (isEditing && id) {
      fetchCustomer(parseInt(id));
    }
  }, [isEditing, id]);

  const fetchCustomer = async (customerId: number) => {
    try {
      setLoading(true);
      const response = await customerApi.getById(customerId);
      form.setFieldsValue(response.data);
    } catch (error) {
      message.error('Failed to fetch customer details');
      navigate(`${rolePrefix}/customers`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      // Optional fields left blank must be omitted, not sent as '' — the
      // server treats empty strings as invalid for optional email/zip/etc.
      // and rejects the update (400). Only first/last/phone are truly required.
      const payload = { ...values };
      ['company_name', 'email', 'address', 'city', 'state', 'zip', 'notes'].forEach((key) => {
        if (typeof payload[key] === 'string' && payload[key].trim() === '') {
          delete payload[key];
        }
      });

      if (isEditing && id) {
        await customerApi.update(parseInt(id), payload);
        message.success('Customer updated successfully');
      } else {
        await customerApi.create(payload);
        message.success('Customer created successfully');
      }

      navigate(`${rolePrefix}/customers`);
    } catch (error) {
      message.error(isEditing ? 'Failed to update customer' : 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`${rolePrefix}/customers`);
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Card>
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          <div>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{ marginBottom: 16 }}
            >
              Back to Customers
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {isEditing ? 'Edit Customer' : 'Add New Customer'}
            </Title>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              preferred_contact_method: 'phone',
            }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="first_name"
                  label="First Name"
                  rules={[{ required: true, message: 'Please enter first name' }]}
                >
                  <Input size="large" placeholder="Enter first name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="last_name"
                  label="Last Name"
                  rules={[{ required: true, message: 'Please enter last name' }]}
                >
                  <Input size="large" placeholder="Enter last name" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="company_name"
              label="Company Name (Optional)"
            >
              <Input size="large" placeholder="Enter company name" />
            </Form.Item>

            <Divider />

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="phone"
                  label="Phone Number"
                  rules={[
                    { required: true, message: 'Please enter phone number' },
                    { pattern: /^[\d\s\-\(\)\+\.]+$/, message: 'Please enter a valid phone number' }
                  ]}
                >
                  <Input size="large" placeholder="(555) 123-4567" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]}
                >
                  <Input size="large" placeholder="customer@example.com" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="preferred_contact_method"
              label="Preferred Contact Method"
            >
              <Select size="large" placeholder="Select preferred contact method">
                <Option value="phone">Phone</Option>
                <Option value="email">Email</Option>
                <Option value="sms">SMS</Option>
              </Select>
            </Form.Item>

            <Divider />

            <Form.Item
              name="address"
              label="Street Address"
            >
              <Input size="large" placeholder="123 Main Street" />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="city"
                  label="City"
                >
                  <Input size="large" placeholder="City" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="state"
                  label="State"
                >
                  <Input size="large" placeholder="State" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="zip"
                  label="ZIP Code"
                  rules={[
                    { pattern: /^\d{5}(-\d{4})?$/, message: 'Please enter a valid ZIP code' }
                  ]}
                >
                  <Input size="large" placeholder="12345" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="notes"
              label="Notes (Optional)"
            >
              <TextArea
                rows={4}
                placeholder="Additional notes about this customer..."
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                disabled={!canEditCustomers}
                icon={<SaveOutlined />}
                block
              >
                {isEditing ? 'Update Customer' : 'Create Customer'}
              </Button>
              {!canEditCustomers && (
                <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>
                  You do not have permission to edit customers.
                </Typography.Text>
              )}
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default CustomerForm;